import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useAnalogClock from "../hooks/useAnalogClock";
import axios from "axios";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCameraRotate, faClock, faUser, faLocationDot } from '@fortawesome/free-solid-svg-icons';

const PunchCard = () => {
  const navigate = useNavigate();
  const { hourRef, minuteRef, secondRef, currentTime } = useAnalogClock();

  const [employeeList, setEmployeeList] = useState([]);
  const [selectedEmpId, setSelectedEmpId] = useState("");
  const [selectedEmpName, setSelectedEmpName] = useState("");
  const [capturedImage, setCapturedImage] = useState(null);
  const [location, setLocation] = useState({ latitude: null, longitude: null });
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [facingMode, setFacingMode] = useState("environment");
  const [cameraButtonText, setCameraButtonText] = useState("Front Camera");
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);
  const [hasCameraPermission, setHasCameraPermission] = useState(true);
  const [isCameraLoading, setIsCameraLoading] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const userid = localStorage.getItem("user_id");

  // ✅ Custom camera hook implementation
  const startCamera = useCallback(async (mode = facingMode) => {
    try {
      setIsCameraLoading(true);
      // Stop any existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      setError(null);
      const constraints = {
        video: {
          facingMode: mode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraButtonText(mode === "user" ? "Back Camera" : "Front Camera");
      setHasCameraPermission(true);
    } catch (err) {
      console.error("Error accessing camera:", err);
      setHasCameraPermission(false);

      // Try the opposite camera if one fails
      const fallbackMode = mode === "user" ? "environment" : "user";
      try {
        const fallbackConstraints = {
          video: { facingMode: fallbackMode },
          audio: false
        };

        const stream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }

        // Update button text based on the fallback camera
        setCameraButtonText(fallbackMode === "user" ? "Back Camera" : "Front Camera");
        setFacingMode(fallbackMode);
        setHasCameraPermission(true);
      } catch (fallbackErr) {
        console.error("Error accessing fallback camera:", fallbackErr);
        setError("कैमरा एक्सेस करने में असमर्थ। कृपया कैमरा अनुमतियों की जाँच करें।");
        setHasCameraPermission(false);
      }
    } finally {
      setIsCameraLoading(false);
    }
  }, [facingMode]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const flipCamera = useCallback(() => {
    const newMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(newMode);
    startCamera(newMode);
  }, [facingMode, startCamera]);

  const captureImage = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) {
      setError("कैमरा तैयार नहीं है");
      return null;
    }

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw current video frame to canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert to data URL
      return canvas.toDataURL('image/jpeg');
    } catch (err) {
      console.error("Error capturing image:", err);
      setError("छवि कैप्चर करने में त्रुटि");
      return null;
    }
  }, []);

  useEffect(() => {
    const getLocation = async () => {
      try {
        setIsSubmitting(true);

        const fallbackLocation = { latitude: 28.6139, longitude: 77.209 }; // Example: Delhi coords

        if (window.Capacitor) {
          console.log("📱 Running in Capacitor");

          const perm = await Geolocation.requestPermissions();
          console.log("Permission:", perm);

          if (perm.location === "granted") {
            try {
              const pos = await Geolocation.getCurrentPosition({
                enableHighAccuracy: true,
                timeout: 10000,
              });
              setLocation({
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
              });
            } catch (e) {
              console.warn("Device location failed, using fallback.");
              setLocation(fallbackLocation);
            }
          } else {
            console.warn("Permission denied, using fallback.");
            setLocation(fallbackLocation);
          }

          setIsSubmitting(false);
          return;
        }

        // ✅ Browser fallback
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              setLocation({
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
              });
              setIsSubmitting(false);
            },
            (err) => {
              console.warn("Browser location failed, using fallback:", err.message);
              setLocation(fallbackLocation);
              setIsSubmitting(false);
            },
            { enableHighAccuracy: true, timeout: 10000 }
          );
        } else {
          console.warn("Geolocation not supported, using fallback.");
          setLocation(fallbackLocation);
          setIsSubmitting(false);
        }
      } catch (err) {
        console.warn("Error fetching location, using fallback:", err.message);
        setLocation({ latitude: 28.6139, longitude: 77.209 }); // fallback
        setIsSubmitting(false);
      }
    };

    getLocation();
  }, []);


  // ✅ Start camera on component mount
  useEffect(() => {
    // Check if browser supports mediaDevices
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError("इस ब्राउज़र में कैमरा समर्थन नहीं है");
      setHasCameraPermission(false);
      return;
    }
    
    startCamera();

    // Cleanup: stop camera when component unmounts
    return () => {
      stopCamera();
    };
  }, []); // Removed dependencies to run only on mount

  // ✅ Fetch employee list
  useEffect(() => {
    fetchEmployeeList();
  }, []);

  const fetchEmployeeList = useCallback(async () => {
    try {
      setStatusMessage("Loading employee list...");
      const response = await axios.get(
        `https://ims-attendanceback.indevconsultancy.in/api/employee-list?kitchen_id=${userid}`
      );
      setEmployeeList(response.data.employeelist || []);
      setStatusMessage("");
    } catch (error) {
      console.error("Error fetching employee list:", error);
      setError("Failed to load employee list. Please check your connection.");
      setStatusMessage("Failed to load employees");
    }
  }, [userid]);

  const handleSelect = useCallback((e) => {
    const { value, options, selectedIndex } = e.target;

    if (!value) return;

    const employeeId = value;
    const employeeName = options[selectedIndex].text;

    // Save in localStorage
    localStorage.setItem("employeeid", employeeId);

    // Update state
    setSelectedEmpId(employeeId);
    setSelectedEmpName(employeeName);

  }, []);


  const dataURLtoBlob = useCallback((dataURL) => {
    if (!dataURL || typeof dataURL !== 'string' || !dataURL.includes(',')) {
      console.error("Invalid dataURL:", dataURL);
      return null;
    }

    try {
      const parts = dataURL.split(',');
      const mimeMatch = parts[0].match(/:(.*?);/);

      if (!mimeMatch || !mimeMatch[1]) {
        console.error("Invalid MIME type in dataURL:", parts[0]);
        return null;
      }

      const mime = mimeMatch[1];
      const bstr = atob(parts[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      for (let i = 0; i < n; i++) {
        u8arr[i] = bstr.charCodeAt(i);
      }
      return new Blob([u8arr], { type: mime });
    } catch (error) {
      console.error("Error converting dataURL to Blob:", error);
      return null;
    }
  }, []);

  const getFormattedPunchTime = useCallback(() => {
    const dateInKolkata = new Date().toLocaleString("en-US", {
      timeZone: "Asia/Kolkata"
    });
    const date = new Date(dateInKolkata);

    // Format to YYYY-MM-DD HH:MM:SS
    const pad = (n) => n.toString().padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }, []);

  const handlePunch = useCallback(async () => {
    if (!selectedEmpId) {
      setError("कृपया कर्मचारी का नाम चुनें");
      return;
    }

    if (isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const imageDataURL = captureImage();
      if (!imageDataURL) {
        setError("कैमरा से छवि प्राप्त नहीं हुई");
        setIsSubmitting(false);
        return;
      }

      const imageBlob = dataURLtoBlob(imageDataURL);
      if (!imageBlob) {
        setError("छवि प्रसंस्करण में त्रुटि");
        setIsSubmitting(false);
        return;
      }

      stopCamera();
      setCapturedImage(imageDataURL);

      const formattedPunchTime = getFormattedPunchTime();

      // Validate required data
      if (!userid) {
        setError("किचन ID गायब है");
        setIsSubmitting(false);
        return;
      }

      if (location.latitude == null || location.longitude == null) {
        setError("स्थान डेटा उपलब्ध नहीं है");
        setIsSubmitting(false);
        return;
      }

      // Prepare FormData
      const formData = new FormData();
      formData.append("user_id", selectedEmpId);
      formData.append("kitcehenid", userid);
      formData.append("entry_time", formattedPunchTime);
      formData.append("latitude", location.latitude);
      formData.append("longitude", location.longitude);
      formData.append("filename", imageBlob, `capture_${Date.now()}.jpg`);

      setStatusMessage("Submitting attendance...");

      const res = await axios.post(
        "https://ims-attendanceback.indevconsultancy.in/api/attendance-submit/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 30000,
        }
      );

      // Handle Response
      if (res.data.status === true) {
        setStatusMessage("✅ उपस्थिति सफलतापूर्वक दर्ज की गई");
        setTimeout(() => {
          navigate("/time-in");
        }, 1500);
      } else {
        setError(res.data.message || `${selectedEmpName} की उपस्थिति पहले से दर्ज है`);
        setTimeout(() => {
          navigate("/kitchen");
        }, 2000);
      }
    } catch (error) {
      console.error("Upload failed:", error.response ? error.response.data : error.message);

      if (error.code === "ECONNABORTED") {
        setError("❌ अनुरोध समय समाप्त! कृपया पुनः प्रयास करें।");
      } else {
        setError("❌ नेटवर्क त्रुटि! कृपया पुनः प्रयास करें।");
      }

      // Restart camera on error
      startCamera();
    } finally {
      setIsSubmitting(false);
    }

  }, [selectedEmpId, selectedEmpName, userid, location, captureImage, dataURLtoBlob, getFormattedPunchTime, stopCamera, startCamera, navigate, isSubmitting]);

  const retryCamera = useCallback(() => {
    setError(null);
    startCamera();
    setCapturedImage(null);
  }, [startCamera]);

  // Check if punch button should be disabled
  const isPunchDisabled =
    isSubmitting ||
    !selectedEmpId ||
    locationPermissionDenied ||
    location.latitude == null ||
    location.longitude == null ||
    !hasCameraPermission;
    
  // Check if camera flip button should be disabled
  const isCameraFlipDisabled = 
    isSubmitting || 
    !!capturedImage || 
    !hasCameraPermission ||
    isCameraLoading;
    
  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-cream">
      <div className="container my-5">
        <div className="card shadow text-center border-0">
          <h4 className="card-header fs-3 fw-semibold bg-primary text-white py-3">
            उपस्थिति पंजीकरण
          </h4>

          <div className="card-body bg-light">
            {/* Status Message */}
            {statusMessage && (
              <div className="alert alert-info d-flex align-items-center justify-content-center" role="alert">
                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                {statusMessage}
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="alert alert-warning d-flex align-items-center" role="alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}

            <div className="row justify-content-center">
              {/* Employee Selection */}
              <div className="col-md-5 mb-4">
                <div className="card h-100">
                  <div className="card-header bg-success text-white">
                    <FontAwesomeIcon icon={faUser} className="me-2" />
                    कर्मचारी का चयन करें
                  </div>
                  <div className="card-body">
                    <select
                      className="form-select form-select-lg"
                      value={selectedEmpId}
                      onChange={handleSelect}
                      disabled={isSubmitting}
                    >
                      <option value="" disabled>नाम चुनें</option>
                      {employeeList.length > 0 ? (
                        employeeList.map((emp) => (
                          <option key={emp.empdetailsid} value={emp.empdetailsid}>
                            {emp.emp_name}
                          </option>
                        ))
                      ) : (
                        <option disabled>कोई कर्मचारी उपलब्ध नहीं है</option>
                      )}
                    </select>
                    {selectedEmpName && (
                      <div className="mt-3 p-2 bg-light rounded">
                        <strong>चयनित कर्मचारी:</strong> {selectedEmpName}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Camera Section */}
              <div className="col-md-5 mb-4">
                <div className="card h-100">
                  <div className="card-header bg-info text-white">
                    <FontAwesomeIcon icon={faCameraRotate} className="me-2" />
                    फोटो खींचें
                  </div>
                  <div className="card-body">
                    <div className="camera-container position-relative">
                      {!hasCameraPermission ? (
                        <div className="d-flex align-items-center justify-content-center bg-dark text-white rounded"
                          style={{ width: "100%", height: "250px" }}>
                          <div className="text-center">
                            <i className="fas fa-camera-slash fa-3x mb-2"></i>
                            <p>कैमरा अनुमति आवश्यक</p>
                            <button 
                              className="btn btn-sm btn-outline-light"
                              onClick={() => startCamera()}>
                              अनुमति दें
                            </button>
                          </div>
                        </div>
                      ) : !capturedImage ? (
                        <div className="position-relative">
                          <video
                            ref={videoRef}
                            autoPlay
                            muted
                            playsInline
                            className="rounded shadow"
                            style={{
                              width: "100%",
                              height: "250px",
                              objectFit: "cover",
                              border: "3px solid #0dcaf0",
                            }}
                          />
                          {isCameraLoading && (
                            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50">
                              <div className="spinner-border text-light" role="status">
                                <span className="visually-hidden">Loading camera...</span>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <img
                          src={capturedImage}
                          alt="Captured"
                          className="rounded shadow"
                          style={{
                            width: "100%",
                            height: "250px",
                            objectFit: "cover",
                            border: "3px solid green",
                          }}
                        />
                      )}

                      <div className="mt-3 d-flex justify-content-center">
                        <button
                          className="btn btn-outline-info btn-sm fw-semibold me-2"
                          onClick={flipCamera}
                          disabled={isCameraFlipDisabled}
                          title="Flip Camera"
                        >
                          <FontAwesomeIcon icon={faCameraRotate} className="me-1" />
                          {isCameraLoading ? "Switching..." : cameraButtonText}
                        </button>
                        {capturedImage && (
                          <button
                            className="btn btn-outline-warning btn-sm fw-semibold"
                            onClick={retryCamera}
                            disabled={isSubmitting}
                          >
                            Retake Photo
                          </button>
                        )}
                      </div>
                    </div>
                    <canvas ref={canvasRef} style={{ display: "none" }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Clock + Location + Punch Button */}
            <div className="row justify-content-center mt-4">
              <div className="col-md-10">
                <div className="card">
                  <div className="card-header bg-secondary text-white">
                    <FontAwesomeIcon icon={faClock} className="me-2" />
                    समय और स्थान
                  </div>
                  <div className="card-body">
                    <div className="row align-items-center">
                      {/* Clock */}
                      <div className="col-md-4 text-center">
                        <div className="mb-3 d-flex justify-content-center">
                          <div className="clock-wrapper">
                            <div className="clock-face">
                              <div ref={hourRef} className="hand hour-hand"></div>
                              <div ref={minuteRef} className="hand minute-hand"></div>
                              <div ref={secondRef} className="hand second-hand"></div>
                            </div>
                          </div>
                        </div>
                        <div className="fw-semibold fs-5">{currentTime}</div>
                      </div>

                      {/* Location */}
                      <div className="col-md-4 text-center">
                        <FontAwesomeIcon icon={faLocationDot} size="2x" className="text-danger mb-2" />
                        <h6>स्थान की जानकारी</h6>
                        {location.latitude && location.longitude ? (
                          <div className="small">
                            <div>अक्षांश: {location.latitude.toFixed(6)}</div>
                            <div>देशांतर: {location.longitude.toFixed(6)}</div>
                          </div>
                        ) : locationPermissionDenied ? (
                          <div className="text-danger small">
                            स्थान अनुमति अस्वीकृत
                          </div>
                        ) : (
                          <div className="text-warning">स्थान लोड हो रहा है...</div>
                        )}
                      </div>

                      {/* Punch Button */}
                      <div className="col-md-4 text-center d-flex align-items-center justify-content-center">
                        <button
                          className="btn btn-primary btn-lg fw-bold px-4 py-3"
                          onClick={handlePunch}
                          disabled={isPunchDisabled}
                        >
                          {isSubmitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                              प्रोसेसिंग...
                            </>
                          ) : (
                            "पंच करें"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card-footer text-muted small py-2">
            तकनीकी साझेदार: Indev Consultancy © {new Date().getFullYear()}
          </div>
        </div>

        {/* CSS for Clock and custom styles */}
        <style>{`
          .clock-wrapper {
            width: 120px;
            height: 120px;
            border: 4px solid #0277bd;
            border-radius: 50%;
            position: relative;
            background: white;
            box-shadow: inset 0 0 15px #ddd;
          }

          .hand {
            position: absolute;
            bottom: 50%;
            left: 50%;
            transform-origin: bottom center;
            transform: rotate(90deg);
          }

          .hour-hand {
            width: 5px;
            height: 30px;
            background: #000;
            z-index: 3;
          }

          .minute-hand {
            width: 3px;
            height: 45px;
            background: #d32f2f;
            z-index: 2;
          }

          .second-hand {
            width: 2px;
            height: 55px;
            background: #ff8f00;
            z-index: 1;
          }
          
          .bg-cream {
            background-color: #f8f9fa;
          }
          
          .card {
            border-radius: 15px;
            overflow: hidden;
          }
        `}</style>
      </div>
    </div>
  );
};

export default PunchCard;